import posthog, { PostHog, PostHogConfig } from 'posthog-js';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { NodeEnvs } from '@finley-technologies/finley-types';

import { PostHogProvider } from 'posthog-js/react';

// Constants
/** These are public keys, so no, they are not secret, and yes, it's okay to check  them into Git. */
const POSTHOG_PUBLIC_KEYS: Record<NodeEnvs, string> = {
  prod: 'phc_ppirhBInCNAX5Wvu47npDiP9eGEe8kDbZgnnNNKATaF',
  stage: 'phc_rofbgpC3RctBxnZc1mKdYzRWxmYnV8gkfaypwZbiyrS',
  development: 'phc_Q7IBQ3Bs8yIEQmRNgNp7nKhpy49RHdrowURiZwTiiiA',
  'client-uat': 'phc_cSJJnQS6hkBhX4AxuvNgzXjB2OEW7GIRIQWHmoLP20i',
  client: 'phc_x5LiCVOGDU4wo1dc2t3l29vjkmYrvpOurBtmBz3JIJA',
};
const POSTHOG_CONFIG: Partial<PostHogConfig> = {
  /* eslint-disable camelcase */
  api_host: 'https://us.i.posthog.com',

  /**
   * Determines whether PostHog should autocapture events.
   * This setting does not affect capturing pageview events (see `capture_pageview`).
   */
  autocapture: {
    element_allowlist: ['a', 'button'],
    capture_copied_text: false,
  },

  /**
   * Determines if cookie should be set on the top level domain (example.com). If PostHog-js is
   * loaded on a subdomain (test.example.com), and `cross_subdomain_cookie` is set to false,
   * it'll set the cookie on the subdomain only (test.example.com).
   */
  cross_subdomain_cookie: false,

  /** A function to be called once the PostHog scripts have loaded successfully. */
  loaded: () => {},

  /** Determines whether PostHog should save referrer information. */
  save_referrer: true,

  /**
   * Determines whether PostHog should save marketing parameters. These are `utm_*` paramaters
   * and friends.
   */
  save_campaign_params: true,

  /** Used to extend the list of campaign parameters that are saved by default. */
  custom_campaign_params: [],

  /** Used to extend the list of user agents that are blocked by default. */
  custom_blocked_useragents: [],

  /**
   * Determines whether PostHog should be in debug mode. You can enable this to get more
   * detailed logging. You can also enable this on your website by appending
   * `?__posthog_debug=true` at the end of your URL, or call `window.posthog.debug()` in your code.
   */
  debug: false,

  /**
   * Determines whether PostHog should capture pageview events automatically.
   * In Finley's case, because our app primarily uses client-side routing, we want to manually
   * hookup PostHog to React Router.
   */
  capture_pageview: false,

  /**
   * Determines whether PostHog should capture pageleave events.
   * If set to `true`, it will capture pageleave events for all pages.
   * If set to `'if_capture_pageview'`, it will only capture pageleave events if `capture_pageview` is also set to `true`.
   */
  capture_pageleave: 'if_capture_pageview',

  /** Determines the number of days to store cookies for. */
  cookie_expiration: 30,

  /** Determines whether PostHog should disable session recording. */
  disable_session_recording: false,

  /** Determines the session recording options. */
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: 'td, [data-pg-no-capture="true"]',
  },

  /**
   * Determines whether PostHog should disable persistence.
   * If set to `true`, the library will not save any data to the browser. It will also delete any data previously saved to the browser.
   */
  disable_persistence: false,

  /** Determines whether PostHog should disable surveys. */
  disable_surveys: true,

  /** Determines whether PostHog should disable web experiments. */
  disable_web_experiments: true,

  /**
   * Determines whether PostHog should disable any external dependency loading.
   * This will prevent PostHog from requesting any external scripts such as those needed for Session Replay, Surveys or Site Apps.
   */
  disable_external_dependency_loading: false,

  /** Determines whether PostHog should capture IP addresses. */
  ip: false,

  /**
   * Determines whether PostHog should respect the Do Not Track header when computing
   * consent in `ConsentManager`.
   *
   * Privacy-focused browsers set a Do Not Track header (set via browser configuration). We want to
   * honor this in production, but it is annoying to unset this header when testing in local or on
   * stage, so we ignore it in those envs.
   */
  respect_dnt: commonUtils.EnvUtils.isCustomerFacing(),

  /** A list of properties that should never be sent with capture calls. */
  property_denylist: [],

  /** A function that is called when a request to the PostHog API fails. */
  on_request_error: () => {},

  /** Prevent autocapture from capturing any attribute names on elements. */
  mask_all_element_attributes: true,

  /** Prevent autocapture from capturing `textContent` on elements. */
  mask_all_text: true,

  /** Custom list of personal data properties to mask. */
  custom_personal_data_properties: [],

  /** Determines whether to capture heatmaps. */
  capture_heatmaps: true,

  /**
   * You can control whether events from PostHog-js have person processing enabled with the
   * person_profiles config setting.
   */
  person_profiles: 'identified_only',
  /* eslint-enable camelcase */
};

// Exports
type AnalyticsProviderProps = {
  clientName: string;
  children: React.ReactNode;
};
export const AnalyticsProvider = ({
  clientName,
  children,
}: AnalyticsProviderProps) => {
  const client = useMemo(
    () =>
      posthog.init(POSTHOG_PUBLIC_KEYS[commonUtils.EnvUtils.getEnv()], {
        ...POSTHOG_CONFIG,
        name: clientName,
      }),
    [clientName],
  );

  // Manually log $pageview whenever the route changes
  const location = useLocation();
  useEffect(() => {
    client.capture('$pageview', {
      // eslint-disable-next-line camelcase
      $current_url: window.location.href,
    });
  }, [client, location]);

  useEffect(() => {
    // Stick the PostHog client on `window.` in dev and staging for debugging purposes
    if (!commonUtils.EnvUtils.isCustomerFacing()) {
      (window as { posthog?: PostHog }).posthog = client;
    }
  }, [client]);

  return <PostHogProvider client={client}>{children}</PostHogProvider>;
};
